import { apiCallPost } from "../fns/util";
import cogoToast from "cogo-toast";

/**
 * Service for fetching branch/site information from MongoDB branchinfos collection
 * Uses the existing getAllSites API endpoint that filters by authStore.prefix
 */
export class BranchService {
  
  /**
   * Fetch all branches for the authenticated user's organization
   * @param {string} prefix - The organization prefix from authStore
   * @param {boolean} sort - Whether to sort branches by name (default: true)
   * @returns {Promise<Array>} Array of branch objects, optionally sorted by name
   */
  static async getAllBranches(prefix, sort = true) {
    try {
      const branches = await apiCallPost(
        `/rai/${prefix}/getAllSites`,
        {}
      );
      
      if (sort && branches.length > 0) {
        return branches.sort((a, b) => {
          const nameA = a.site || a.name || a.branchName || a.siteName || '';
          const nameB = b.site || b.name || b.branchName || b.siteName || '';
          return nameA.localeCompare(nameB);
        });
      }
      
      return branches;
    } catch (err) {
      console.error('Error fetching branches:', err);
      cogoToast.error('Failed to load branch information. Please try again.');
      throw err;
    }
  }

  /**
   * Extract branch names from branch objects
   * Handles different possible field names for branch/site names
   * @param {Array} branches - Array of branch objects
   * @param {boolean} sort - Whether to sort the names alphabetically (default: true)
   * @returns {Array<string>} Array of branch names, sorted alphabetically
   */
  static extractBranchNames(branches, sort = true) {
    const branchNames = branches.map(branch => {
      // Handle different possible field names for the branch name
      return branch.site || branch.name || branch.branchName || branch.siteName || branch;
    });
    
    return sort ? branchNames.sort((a, b) => a.localeCompare(b)) : branchNames;
  }

  /**
   * Get branch names directly as an array of strings
   * @param {string} prefix - The organization prefix from authStore
   * @param {boolean} sort - Whether to sort the names alphabetically (default: true)
   * @returns {Promise<Array<string>>} Array of branch names, sorted alphabetically
   */
  static async getBranchNames(prefix, sort = true) {
    try {
      const branches = await this.getAllBranches(prefix);
      return this.extractBranchNames(branches, sort);
    } catch (err) {
      console.error('Error getting branch names:', err);
      return []; // Return empty array as fallback
    }
  }

  /**
   * Find a specific branch by name
   * @param {string} prefix - The organization prefix from authStore
   * @param {string} branchName - Name of the branch to find
   * @returns {Promise<Object|null>} Branch object or null if not found
   */
  static async findBranchByName(prefix, branchName) {
    try {
      const branches = await this.getAllBranches(prefix, false); // Don't need sorting for find operation
      return branches.find(branch => 
        (branch.site || branch.name || branch.branchName || branch.siteName || branch) === branchName
      ) || null;
    } catch (err) {
      console.error('Error finding branch:', err);
      return null;
    }
  }
}

export default BranchService;
